import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useReactToPrint } from "react-to-print";
import {
  Search,
  ChevronDown,
  ChevronRight,
  MapPin,
  User,
  Printer,
  Truck,
  Package,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Plus,
  DollarSign,
  Layers,
  Hash,
  Send,
  AlertTriangle,
  // ✨ NEW: icons for production status
  FlaskConical,
  CheckCircle2,
  CircleDashed,
  CircleAlert,
  Calendar,
  Weight,
  Loader2,
  FileText,
  History
} from "lucide-react";

import Loader from "../../../loader/Loader";
import Pagination from "../../Pagination";
import { domain, domainAccounting } from "../../../../security";
import { selectUserID, selectLocationId } from "../../../../redux/IchthusSlice";

import AddDeliveryOrder from "./AddDeliveryOrder";
import { DeliveryOrderPrint } from "./DeliveryOrderPrint";

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
  }).format(val || 0);

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

const fmt = (val) =>
  new Intl.NumberFormat("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val ?? 0);

// ─────────────────────────────────────────────────────────────
// ✨ NEW: PRODUCTION STATUS HELPERS
// Computes whether a DO's production is None / Partial / Complete.
// "Complete" = every deliveryOrderItem has at least one production record.
// "Partial"  = some but not all items are covered.
// "None"     = no productions at all for this DO.
// ─────────────────────────────────────────────────────────────

/**
 * @param {object} do_  - The delivery order object (with deliveryOrderItems[])
 * @param {Array}  prods - All production records for this DO
 * @returns {"none"|"partial"|"complete"}
 */
const computeProductionStatus = (do_, prods) => {
  if (!prods || prods.length === 0) return "none";
  const itemIds = (do_.deliveryOrderItems || []).map((i) => i.id);
  if (itemIds.length === 0) return "none";
  const coveredIds = new Set(prods.map((p) => p.deliveryOrderItemId));
  const coveredCount = itemIds.filter((id) => coveredIds.has(id)).length;
  if (coveredCount === 0) return "none";
  if (coveredCount >= itemIds.length) return "complete";
  return "partial";
};

// ─────────────────────────────────────────────────────────────
// ✨ NEW: PRODUCTION STATUS BADGE COMPONENT
// ─────────────────────────────────────────────────────────────

const ProductionStatusBadge = ({ status, count }) => {
  if (status === "none") return null;

  const cfg = {
    partial: {
      bg: "bg-amber-50",
      text: "text-amber-700",
      border: "border-amber-200",
      icon: <CircleAlert size={10} />,
      label: "Partial Production",
    },
    complete: {
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      border: "border-emerald-200",
      icon: <CheckCircle2 size={10} />,
      label: "Fully Produced",
    },
  }[status];

  if (!cfg) return null;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${cfg.bg} ${cfg.text} ${cfg.border}`}
    >
      {cfg.icon}
      {cfg.label}
      {count > 0 && <span className="ml-0.5 opacity-75">({count})</span>}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────
// ✨ NEW: PRODUCTION RECORDS SECTION (for expanded DO row)
// Shows all production records linked to a DO, per item.
// ─────────────────────────────────────────────────────────────

const DOProductionSection = ({ do_, productions, isLoading }) => {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-indigo-500 text-sm">
        <Loader2 size={16} className="animate-spin" />
        <span className="font-semibold">Loading production records…</span>
      </div>
    );
  }

  const doItems = do_.deliveryOrderItems || [];
  const status = computeProductionStatus(do_, productions);

  return (
    <div className="mt-4">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-bold text-gray-700 text-xs uppercase tracking-wide flex items-center gap-1.5">
          <FlaskConical size={13} className="text-emerald-600" />
          Production Records
        </h4>
        {productions.length > 0 && (
          <ProductionStatusBadge status={status} count={productions.length} />
        )}
      </div>

      {/* Per-Item Coverage Grid */}
      {doItems.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-3">
          {doItems.map((item) => {
            const itemProductions = productions.filter(
              (p) => p.deliveryOrderItemId === item.id,
            );
            const hasProduction = itemProductions.length > 0;
            const totalWeight = itemProductions.reduce(
              (sum, p) => sum + (p.totalActualWeight || 0),
              0,
            );

            return (
              <div
                key={item.id}
                className={`flex items-center gap-2 p-2.5 rounded-lg border text-xs transition-all ${
                  hasProduction
                    ? "bg-emerald-50 border-emerald-200"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                {hasProduction ? (
                  <CheckCircle2
                    size={14}
                    className="text-emerald-500 shrink-0"
                  />
                ) : (
                  <CircleDashed size={14} className="text-gray-300 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p
                    className={`font-bold truncate ${
                      hasProduction ? "text-emerald-900" : "text-gray-500"
                    }`}
                  >
                    {item.productName}
                  </p>
                  {hasProduction ? (
                    <p className="text-emerald-600 font-semibold">
                      {itemProductions.length} production(s) ·{" "}
                      {fmt(totalWeight)} kg
                    </p>
                  ) : (
                    <p className="text-gray-400 italic">No production yet</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Production Records List */}
      {productions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-6 text-gray-400 bg-gray-50 rounded-xl border border-dashed border-gray-200">
          <FlaskConical size={28} className="opacity-20 mb-2" />
          <p className="text-sm font-medium text-gray-500">
            No production records yet
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            Create a production record from the Productions module.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {productions.map((prod) => {
            const doItem = doItems.find(
              (i) => i.id === prod.deliveryOrderItemId,
            );

            // Group entries by tagName for summary display
            const entryGroups = prod.entries?.reduce((acc, e) => {
              const key = e.tagName || e.groupName || "Material";
              if (!acc[key]) acc[key] = { qty: 0, unit: e.unit || "" };
              acc[key].qty += e.quantity || 0;
              return acc;
            }, {});

            return (
              <div
                key={prod.id}
                className="bg-white border border-emerald-100 rounded-xl p-4 shadow-sm"
              >
                {/* Production header row */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="p-1.5 bg-emerald-100 rounded-lg shrink-0">
                      <FlaskConical size={13} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-black text-emerald-900 text-sm leading-tight">
                        {prod.productionNumber}
                      </p>
                      {doItem && (
                        <p className="text-[10px] font-bold text-emerald-600 mt-0.5">
                          → {doItem.productName}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-right shrink-0">
                    <div>
                      <div className="flex items-center gap-1 text-xs text-gray-400 justify-end">
                        <Calendar size={10} />
                        {formatDate(prod.productionDate)}
                      </div>
                      <div className="flex items-center gap-1 text-sm font-black text-emerald-700 justify-end mt-0.5">
                        <Weight size={11} />
                        {fmt(prod.totalActualWeight)} kg
                      </div>
                    </div>
                  </div>
                </div>

                {/* Entry summary pills */}
                {entryGroups && Object.keys(entryGroups).length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(entryGroups).map(([tag, data]) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-50 border border-slate-200 rounded-full text-[10px] font-bold text-slate-600"
                      >
                        {tag}:{" "}
                        <span className="text-indigo-600">
                          {fmt(data.qty)} {data.unit}
                        </span>
                      </span>
                    ))}
                  </div>
                )}

                {/* Remarks */}
                {prod.remarks && (
                  <p className="mt-2 text-[10px] text-gray-400 italic border-t border-gray-50 pt-2">
                    {prod.remarks}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// STATUS BADGE (existing, unchanged)
// ─────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const s = (status || "").toLowerCase();
  let color = "bg-gray-50 text-gray-600 border-gray-200";
  let icon = <AlertCircle size={11} />;
  let displayText = status;

  if (s === "sold") {
    color = "bg-amber-50 text-amber-700 border-amber-200";
    icon = <Package size={11} />;
    displayText = "Preparation";
  } else if (s === "dispatched") {
    color = "bg-blue-50 text-blue-700 border-blue-200";
    icon = <Truck size={11} />;
    displayText = "Dispatched";
  } else if (s === "delivered") {
    color = "bg-green-50 text-green-700 border-green-200";
    icon = <CheckCircle size={11} />;
  } else if (s === "partial" || s === "pending") {
    color = "bg-blue-50 text-blue-700 border-blue-200";
    icon = <Clock size={11} />;
  } else if (s === "voided") {
    color = "bg-red-50 text-red-700 border-red-200";
    icon = <XCircle size={11} />;
  }

  return (
    <div
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${color}`}
    >
      {icon}
      <span>{displayText}</span>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// STATS CARD (existing, unchanged)
// ─────────────────────────────────────────────────────────────

const StatsCard = ({ label, value, colorClass, icon }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-3">
    <div className={`p-2 rounded-xl ${colorClass}`}>
      {React.cloneElement(icon, { size: 18 })}
    </div>
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-800">{value}</p>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────

const AllDeliveryOrders = () => {
  const userId = useSelector(selectUserID);
  const userLocationId = useSelector(selectLocationId);

  const [deliveryOrders, setDeliveryOrders] = useState([]);
  const [pendingSyncData, setPendingSyncData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("All");
  const [expandedId, setExpandedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [allLocationsList, setAllLocationsList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [showAddModal, setShowAddModal] = useState(false);

  // Dispatch Modal States
  const [dispatchTarget, setDispatchTarget] = useState(null);
  const [isDispatching, setIsDispatching] = useState(false);

  // Print
  const printRef = useRef(null);
  const [printTarget, setPrintTarget] = useState(null);
  const handlePrint = useReactToPrint({ content: () => printRef.current });
  const triggerPrint = (doItem) => {
    setPrintTarget(doItem);
    setTimeout(() => handlePrint(), 200);
  };

  // ✨ NEW: Productions state
  // productionsByDO: { [doId: number]: Production[] }
  // productionsLoading: set of doIds currently being fetched
  const [productionsByDO, setProductionsByDO] = useState({});
  const [productionsLoading, setProductionsLoading] = useState(new Set());

  // ── Fetch ──
  // ✨ CHANGED: Also fetch all productions so we can show status badges
  // in the table rows without waiting for the user to expand a row.
  const fetchDeliveryOrders = useCallback(async () => {
    setLoading(true);
    try {
      const [doRes, locRes, prodRes] = await Promise.all([
        axios.get(`${domain}/api/DeliveryOrderMain`),
        axios.get(`${domain}/api/Locations`),
        axios.get(`${domain}/api/Productions`), // ✨ NEW
      ]);

      const locations = Array.isArray(locRes.data)
        ? locRes.data
        : (locRes.data?.data ?? []);
      setAllLocationsList(locations);

      const data = Array.isArray(doRes.data)
        ? doRes.data
        : (doRes.data?.data ?? []);
      setDeliveryOrders(data);

      // ✨ NEW: Group productions by deliveryOrderId
      const grouped = {};
      (Array.isArray(prodRes.data) ? prodRes.data : []).forEach((p) => {
        if (!p.deliveryOrderId) return;
        if (!grouped[p.deliveryOrderId]) grouped[p.deliveryOrderId] = [];
        grouped[p.deliveryOrderId].push(p);
      });
      setProductionsByDO(grouped);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error("Failed to load Delivery Orders.");

    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeliveryOrders();
  }, [fetchDeliveryOrders]);

  // Auto-set location from Redux
  useEffect(() => {
    if (allLocationsList.length > 0 && selectedLocation === "") {
      if (userLocationId) {
        const userLoc = allLocationsList.find(
          (l) => l.id === parseInt(userLocationId),
        );
        setSelectedLocation(userLoc ? userLoc.locationName : "All");
      } else {
        setSelectedLocation("All");
      }
    }
  }, [allLocationsList, userLocationId, selectedLocation]);

  // ✨ NEW: When a row expands, fetch detailed productions for that DO
  // (includes full entry data for the expanded production cards).
  // We skip if already loaded.
  const fetchProductionsForDO = useCallback(
    async (doId) => {
      if (productionsByDO[doId]?.some((p) => p.entries !== undefined)) return; // already detailed
      if (productionsLoading.has(doId)) return;

      setProductionsLoading((prev) => new Set([...prev, doId]));
      try {
        const res = await axios.get(
          `${domain}/api/Productions/by-delivery-order/${doId}`,
        );
        setProductionsByDO((prev) => ({
          ...prev,
          [doId]: res.data || [],
        }));
      } catch {
        // silently fail — keep the partial data from the initial load
      } finally {
        setProductionsLoading((prev) => {
          const next = new Set(prev);
          next.delete(doId);
          return next;
        });
      }
    },
    [productionsByDO, productionsLoading],
  );

  // Trigger detail fetch when a row expands
  useEffect(() => {
    if (expandedId) fetchProductionsForDO(expandedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedId]);

  // ✨ NEW: Helper selectors
  const getProductionsForDO = useCallback(
    (doId) => productionsByDO[doId] || [],
    [productionsByDO],
  );

  const getProductionStatus = useCallback(
    (do_) => computeProductionStatus(do_, getProductionsForDO(do_.id)),
    [getProductionsForDO],
  );

  // ── Action: Confirm Dispatch ──
  const confirmDispatch = async () => {
    if (!dispatchTarget) return;
    setIsDispatching(true);
    try {
      await axios.post(
        `${domain}/api/DeliveryOrders/dispatch/${dispatchTarget.id}`,
        {
          userId: userId?.toString(),
        },
      );
      toast.success("Order dispatched successfully!");
      fetchDeliveryOrders();
      setDispatchTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to dispatch order.");
    } finally {
      setIsDispatching(false);
    }
  };

  // ── Tabs ──
  const tabs = [
    { id: "All", label: "All", icon: <Layers size={14} /> },
    { id: "Sold", label: "Order Preparation", icon: <Package size={14} /> },
    { id: "Dispatched", label: "Dispatched", icon: <Truck size={14} /> },
    { id: "Voided", label: "Voided", icon: <XCircle size={14} /> },
  ];

  const tabMatch = (do_, tab) => {
    if (tab === "All") return true;
    const s = (do_.status || "").toLowerCase();
    if (tab === "Sold") return s === "sold" && !do_.isVoid;
    if (tab === "Dispatched")
      return (s === "dispatched" || s === "delivered") && !do_.isVoid;
    if (tab === "Voided") return s === "voided" || do_.isVoid;
    return true;
  };

  // ── Location names for dropdown ──
  const locationNames = [
    "All",
    ...new Set(allLocationsList.map((l) => l.locationName)).values(),
  ].sort((a, b) => (a === "All" ? -1 : b === "All" ? 1 : a.localeCompare(b)));

  // ── Filtered list ──
  const filtered = deliveryOrders.filter((do_) => {
    const matchTab = tabMatch(do_, activeTab);
    const matchSearch =
      !searchTerm ||
      (do_.deliveryOrderNumber || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (do_.customerName || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (do_.salesOrderNumber || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    const matchLocation =
      selectedLocation === "All" ||
      selectedLocation === "" ||
      do_.locationName === selectedLocation;
    return matchTab && matchSearch && matchLocation;
  });

  const currentDOs = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const [selectedDO, setSelectedDO] = useState(null);

  const fetchDataDOById = useCallback(async (id) => {
    try {
      const response = await axios.get(`${domain}/api/DeliveryOrderMain/${id}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return null
    }
  }, []);

  const toggleExpandDO = async (id) => {
    setLoading(true)
    let _selectedDO = null
    if(expandedId !== id)
    {
      _selectedDO = await fetchDataDOById(id)
    }
    setSelectedDO(_selectedDO)

    setExpandedId(expandedId === id ? null : id)
    setLoading(false)
  };

  const tabCount = (tab) =>
    deliveryOrders.filter((d) => tabMatch(d, tab)).length;

  // ── Stats ──
  const stats = {
    total: deliveryOrders.length,
    delivered: deliveryOrders.filter(
      (d) =>
        !d.isVoid &&
        (d.status?.toLowerCase() === "dispatched" ||
          d.status?.toLowerCase() === "delivered"),
    ).length,
    voided: deliveryOrders.filter(
      (d) => d.isVoid || d.status?.toLowerCase() === "voided",
    ).length,
    netValue: deliveryOrders
      .filter((d) => !d.isVoid)
      .reduce((a, b) => a + (b.totalAmount || 0), 0),
  };

  const [showSyncToAccountingForm, setShowSyncToAccountingForm] = useState(false);
  const openSyncToAccountingForm = () => {
    setShowSyncToAccountingForm(true);
  }
  const closeSyncToAccountingForm = () => {
    setShowSyncToAccountingForm(false);
  }

  const [showAverageCostConfig, setShowAverageCostConfig] = useState(false)
  const [itemsWithoutAverageCost, setItemsWithoutAverageCost] = useState(false)
  const openAverageCostConfig = (_data) => {
    setShowAverageCostConfig(true);
    setItemsWithoutAverageCost(_data)
  }
  const closeAverageCostConfig = () => {
    setShowAverageCostConfig(false);
    setPendingSyncData(null); // Clear pending data if user cancels
  }
  const handleSaveCostsAndContinue = async (updatedCosts) => {
    let transaction = [];
    console.log({updatedCosts})
    console.log({deliveryOrders})

    deliveryOrders.forEach(order => {

      order?.soReference?.forEach(ref => {
        ref?.salesOrderItems?.forEach(item => {
          if(item?.bomComponents?.length === 0){
            const mainDataUpdated = updatedCosts?.find(
              i => i.itemCode === item.itemCode
            );

            if(mainDataUpdated){
              const _mainQuantity = (item.deliveredQty || 0);

              const _mainAverageCost =
                  mainDataUpdated.averageCost || 0;

                const _mainInventoryValue =
                  _mainAverageCost * _mainQuantity;

              transaction.push({
                referenceNumber: ref?.soNumber,
                itemCode: item.itemCode,
                quantity: _mainQuantity,
                inventoryValue: _mainInventoryValue,
                averageCost: 0
              });
            }
          }
          
          // COMPONENT
          item?.bomComponents?.forEach(comp => {

            const dataUpdated = updatedCosts?.find(
              i => i.itemCode === comp.componentProduct?.itemCode
            );

            if (dataUpdated) {
              const _quantity =
                (comp.quantity || 0) * (item.quantity || 0);

              const _averageCost = dataUpdated.averageCost || 0;
              const _inventoryValue = _averageCost * _quantity;

              const _data = {
                referenceNumber: ref?.soNumber,
                itemCode: comp.componentProduct?.itemCode,
                quantity: _quantity,
                inventoryValue: _inventoryValue,
                averageCost: 0 // automated in the API
              };

              transaction.push(_data);
            }
          });
        });
      });
    });

    try {
      setLoading(true);
      
      // 1. Update the missing average costs in the DB
      await axios.post(`${domainAccounting}/api/Item/update-average-costs`, transaction);
      
      toast.success("Average costs updated.");

      // 2. Close the modal
      closeAverageCostConfig();

      // 3. PROCEED with the sync using the data we saved in state earlier
      if (pendingSyncData) {
        await proceedWithSync(pendingSyncData);
      }

    } catch (error) {
      console.error("Failed to update costs", error);
      toast.error("Failed to update costs. Sync cancelled.");
    } finally {
      setLoading(false);
    }
  };

  const getItemsWithoutAverageCost = async (itemCodes) => {
    try {
      const response = await axios.post(
        `${domainAccounting}/api/Item/check-no-average-cost-items`,
        itemCodes,
      );

      return response.data

    } catch (error) {
      console.error(error);
      return []
    }
  };

  // Function to handle the final step (The actual POST to Accounting)
  const proceedWithSync = async (dataToSync) => {
    try {
      setLoading(true);
      await axios.post(
        `${domainAccounting}/api/DeliveryOrder/bulk`,
        dataToSync,
      );

      toast.success(`Successfully pushed ${dataToSync?.length || 0} to Accounting!`);
      setPendingSyncData(null); // Clear the temporary data
    } catch (error) {
      console.error(error);
      toast.error(`Failed to push to Accounting.`);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncToAccounting = async (dateFrom, dateTo) => {
    if (deliveryOrders.length <= 0) {
      toast.error(`No data to Sync.`);
      return;
    }

    setLoading(true);

    try {
      const getDateOnly = (value) => {
        const d = new Date(value);
        d.setHours(0, 0, 0, 0);
        return d.getTime();
      };

      const from = getDateOnly(dateFrom);
      const to = getDateOnly(dateTo);

      const filtered = deliveryOrders.filter((i) => {
        const date = getDateOnly(i.deliveryDate);
        return date >= from && date <= to;
      });

      let itemCodes = [];

      const finalData = filtered.map((dOrder) => {
        const amount = (dOrder.soReference || []).reduce((soSum, so) => {
          return soSum + (so.salesOrderItems || []).reduce((lineSum, line) => {
              return lineSum + (Number(line.unitPrice) || 0) * (Number(line.deliveredQty) || 0);
            }, 0);
        }, 0);

        return {
          deliveryOrderDetails: dOrder.soReference?.map((item) => ({
            salesOrderSONumber: item.soNumber,
            deliveryOrderDetailLines: item.salesOrderItems?.map((line) => {
              itemCodes.push(line.itemCode);
              return {
                itemCode: line.itemCode,
                orderedQty: line.quantity,
                receivedQty: line.deliveredQty,
                remainingQty: line.remainingQty ?? 0,
                unitPrice: line.unitPrice,
                subTotal: line.lineTotal,
                taxationCodeId: 0,
                totalDiscount: line?.totalDiscount,
                taxAmount: line?.taxAmount,
                totalAmount: line?.totalAmount,
              };
            }),
          })),
          doNumber: dOrder.deliveryOrderNumber,
          customerId: dOrder.customerCode,
          amount: amount,
          doDate: dOrder.deliveryDate,
          uploadedFile: "",
          notes: dOrder?.remarks,
          status: dOrder.status,
          createdDate: new Date().toISOString(),
          createdBy: userId || 0,
        };
      });

      // Check for items without average cost
      // Use Set to remove duplicate item codes before checking
      const uniqueItemCodes = [...new Set(itemCodes)];
      const _itemsWithoutAverageCost = await getItemsWithoutAverageCost(uniqueItemCodes);

      if (_itemsWithoutAverageCost.length > 0) {
        // STOP HERE: Store data and open modal
        setPendingSyncData(finalData); 
        openAverageCostConfig(_itemsWithoutAverageCost);
        setLoading(false); // Stop loading while user types
        return; 
      }

      // If no missing costs, proceed immediately
      await proceedWithSync(finalData);

    } catch (error) {
      console.error(error);
      toast.error(`An error occurred during preparation.`);
      setLoading(false);
    }
  };

  // const handleSyncToAccounting = async (dateFrom, dateTo) => {
  //   if(deliveryOrders.length <= 0){
  //     toast.error(`No data to Sync.`);
  //     return
  //   }

  //   setLoading(true)

  //   try {
  //     const getDateOnly = value => {
  //       const d = new Date(value);
  //       d.setHours(0, 0, 0, 0);
  //       return d.getTime();
  //     };

  //     const filtered = deliveryOrders.filter(i => {
  //       const date = getDateOnly(i.deliveryDate);
  //       const from = getDateOnly(dateFrom);
  //       const to = getDateOnly(dateTo);

  //       return date >= from && date <= to;
  //     });

  //     let itemCodes = [];

  //     const finalData = filtered?.filter(i => i.deliveryDate >= dateFrom && i.deliveryDate <= dateTo).map(dOrder => {
  //       const amount = (dOrder.soReference || []).reduce((soSum, so) => {
  //           return soSum + (so.salesOrderItems || []).reduce((lineSum, line) => {
  //             return lineSum + ((Number(line.unitPrice) || 0) * (Number(line.deliveredQty) || 0));
  //           }, 0);
  //         }, 0);

  //       return {
  //         deliveryOrderDetails: dOrder.soReference?.map((item) => ({
  //           salesOrderSONumber: item.soNumber,

  //           deliveryOrderDetailLines: item.salesOrderItems?.map((line) => {
  //             itemCodes.push(line.itemCode);
              
  //             return ({
  //               itemCode: line.itemCode,
  //               orderedQty: line.quantity,
  //               receivedQty: line.deliveredQty,
  //               remainingQty: line.remainingQty ?? 0,
  //               unitPrice: line.unitPrice,
  //               subTotal: line.lineTotal,
  //               taxationCodeId: 0,
  //               totalDiscount: line?.totalDiscount,
  //               taxAmount: line?.taxAmount,
  //               totalAmount: line?.totalAmount
  //           })})
  //         })),

  //         doNumber: dOrder.deliveryOrderNumber,
  //         customerId: dOrder.customerCode,
  //         amount: amount,
  //         doDate: dOrder.deliveryDate,
  //         uploadedFile: "",
  //         notes: dOrder?.remarks,
  //         status: dOrder.status,
  //         createdDate: new Date().toISOString(),
  //         createdBy: userId || 0,
  //       }
  //     });

  //     const _itemsWithoutAverageCost = await getItemsWithoutAverageCost(itemCodes)

  //     if(_itemsWithoutAverageCost.length > 0){
  //       openAverageCostConfig(_itemsWithoutAverageCost)
  //     }

  //     //i will block here since i want to config the average cost first, if Update Costs & Continue, then it will proceed here

  //     await axios.post(
  //       `${domainAccounting}/api/DeliveryOrder/bulk`,
  //       finalData,
  //     );

  //     toast.success(
  //       `Successfully pushed ${deliveryOrders?.length || 0 } to Accounting!`,
  //     );

  //   } catch (error) {
  //     console.error(error);
  //     toast.error(`Failed to push "order"} to Accounting.`);
  //   } finally {
  //     setLoading(false)
  //   }
  // };

  return (
    <div className="min-h-screen pb-12 bg-gray-50">
      {/* Hidden print target */}
      <div className="hidden">
        {printTarget && (
          <DeliveryOrderPrint ref={printRef} data={printTarget} />
        )}
      </div>

      {/* Add DO Modal */}
      {showAddModal && (
        <AddDeliveryOrder
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            fetchDeliveryOrders();
            setShowAddModal(false);
          }}
        />
      )}

      {/* Dispatch Confirmation Modal */}
      {dispatchTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                  <Send className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Dispatch Order
                  </h3>
                  <p className="text-sm text-gray-500 font-medium mt-0.5">
                    {dispatchTarget.deliveryOrderNumber}
                  </p>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 text-amber-800">
                <AlertTriangle className="shrink-0 mt-0.5" size={18} />
                <div className="text-sm">
                  <p className="font-bold mb-1">Are you absolutely sure?</p>
                  <p className="opacity-90">
                    This will mark the order as dispatched. Once dispatched, the
                    order <strong>cannot be reverted</strong> back to the
                    preparation stage.
                  </p>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-100 bg-gray-50 p-4 flex justify-end gap-3">
              <button
                onClick={() => setDispatchTarget(null)}
                disabled={isDispatching}
                className="px-4 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDispatch}
                disabled={isDispatching}
                className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition disabled:opacity-70 disabled:cursor-not-allowed shadow-sm"
              >
                {isDispatching ? (
                  <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></span>
                ) : (
                  <Send size={16} />
                )}
                Confirm Dispatch
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sticky Header ── */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-xl">
                <Truck size={22} className="text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Delivery Orders
                </h1>
                <p className="text-gray-500 text-sm mt-0.5">
                  Manage outbound deliveries · Portioned delivery supported
                </p>
              </div>
            </div>

            <div className="flex flex-row gap-3">
              <button
                onClick={() => openSyncToAccountingForm()}
                className="flex items-center gap-2 px-3.5 py-2 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-xl text-sm font-medium transition"
              >
                <History size={16} />
                <span className="hidden sm:inline">Sync To Accounting</span>
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold transition shadow-sm"
              >
                <Plus size={16} />
                Add Delivery Order
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-5 p-1 bg-gray-100 rounded-xl max-w-max overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setCurrentPage(1);
                }}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {tab.icon}
                {tab.label}
                <span
                  className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
                    activeTab === tab.id
                      ? "bg-blue-50 text-blue-600"
                      : "bg-gray-200 text-gray-500"
                  }`}
                >
                  {tabCount(tab.id)}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-5">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <StatsCard
            label="Total DOs"
            value={stats.total}
            colorClass="bg-gray-100 text-gray-600"
            icon={<Layers />}
          />
          <StatsCard
            label="Dispatched"
            value={stats.delivered}
            colorClass="bg-blue-50 text-blue-600"
            icon={<Truck />}
          />
          <StatsCard
            label="Voided"
            value={stats.voided}
            colorClass="bg-red-50 text-red-600"
            icon={<XCircle />}
          />
          <StatsCard
            label="Net Value"
            value={formatCurrency(stats.netValue)}
            colorClass="bg-indigo-50 text-indigo-600"
            icon={<DollarSign />}
          />
        </div>

        {/* Search + Location */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-200 mb-5">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by DO#, SO#, or customer..."
                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all text-sm outline-none"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="relative min-w-[180px]">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                value={selectedLocation}
                onChange={(e) => {
                  setSelectedLocation(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-9 pr-8 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-400 appearance-none bg-white text-sm outline-none"
              >
                {locationNames.map((loc) => (
                  <option key={loc} value={loc}>
                    {loc === "All" ? "All Locations" : loc}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-24">
            <Loader />
          </div>
        ) : (
          <>
            {/* ── Desktop Table ── */}
            <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <table className="w-full text-sm text-left text-gray-600">
                <thead className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="w-10 px-5 py-3.5"></th>
                    <th className="px-5 py-3.5 font-semibold">DO Number</th>
                    <th className="px-5 py-3.5 font-semibold">SO Reference</th>
                    <th className="px-5 py-3.5 font-semibold">Customer</th>
                    <th className="px-5 py-3.5 font-semibold">Delivery Date</th>
                    <th className="px-5 py-3.5 font-semibold text-center">
                      Status
                    </th>
                    {/* ✨ NEW: Production column header */}
                    <th className="px-5 py-3.5 font-semibold text-center text-emerald-700">
                      Production
                    </th>
                    <th className="px-5 py-3.5 font-semibold text-right">
                      Amount
                    </th>
                    <th className="px-5 py-3.5 font-semibold text-center">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {currentDOs.map((do_) => {
                    // ✨ NEW: compute production status per row
                    const prods = getProductionsForDO(do_.id);
                    const prodStatus = getProductionStatus(do_);

                    return (
                      <React.Fragment key={do_.id}>
                        <tr
                          onClick={() =>
                            toggleExpandDO(do_.id)
                          }
                          className={`hover:bg-blue-50/30 transition cursor-pointer ${
                            expandedId === do_.id ? "bg-blue-50/20" : ""
                          } ${do_.isVoid ? "opacity-60" : ""}`}
                        >
                          <td className="px-5 py-3.5 text-center">
                            <button className="p-1 hover:bg-gray-200 rounded-lg transition text-gray-400">
                              {expandedId === do_.id ? (
                                <ChevronDown size={16} />
                              ) : (
                                <ChevronRight size={16} />
                              )}
                            </button>
                          </td>
                          <td className="px-5 py-3.5">
                            <span className="font-bold text-gray-900">
                              {do_.deliveryOrderNumber}
                            </span>
                            {do_.hasDebitMemo && (
                              <span className="ml-2 text-[9px] bg-orange-100 text-orange-700 border border-orange-200 px-1.5 py-0.5 rounded font-bold uppercase">
                                LOCKED
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex flex-col gap-4">
                              {do_.soReference.map((item, index) => (
                                <div key={index} className="group flex flex-col">
                                  {/* PO Number Line */}
                                  <div className="flex items-center gap-2 text-sm font-bold text-slate-800">
                                    <FileText size={14} className="text-indigo-500" />
                                    <span>{item?.soNumber}</span>
                                  </div>
                                  
                                  {/* Location Line */}
                                  <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5">
                                    <MapPin size={12} className="text-slate-400" />
                                    <span className="truncate max-w-[150px]">{item.locationName}</span>
                                  </div>
                                  <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5">
                                    <User size={12} className="text-slate-400" />
                                    <span className="truncate max-w-[150px]">{item.salesmanName}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </td>
                          <td className="px-5 py-3.5">
                            <p className="font-medium text-gray-900">
                              {do_.customerName}
                            </p>
                            <p className="text-xs text-gray-400">
                              {do_.locationName}
                            </p>
                          </td>
                          <td className="px-5 py-3.5 text-gray-500">
                            {formatDate(do_.deliveryDate || do_.date)}
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            <StatusBadge
                              status={do_.isVoid ? "Voided" : do_.status}
                            />
                          </td>

                          {/* ✨ NEW: Production status cell */}
                          <td className="px-5 py-3.5 text-center">
                            {prodStatus === "none" ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border bg-gray-50 text-gray-400 border-gray-200">
                                <CircleDashed size={10} />
                                Not Started
                              </span>
                            ) : (
                              <ProductionStatusBadge
                                status={prodStatus}
                                count={prods.length}
                              />
                            )}
                          </td>

                          <td className="px-5 py-3.5 text-right">
                            <div className="flex flex-col items-end">
                              <span className="font-bold text-gray-900">
                                {formatCurrency(do_.totalAmount)}
                              </span>
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            <div
                              className="flex items-center justify-center gap-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {do_.status?.toLowerCase() === "sold" &&
                                !do_.isVoid && (
                                  <button
                                    onClick={() => setDispatchTarget(do_)}
                                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wide text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition shadow-sm"
                                  >
                                    For Dispatch <Send size={13} />
                                  </button>
                                )}
                              <button
                                onClick={() => triggerPrint(do_)}
                                title="Print"
                                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
                              >
                                <Printer size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* ── Expanded Detail ── */}
                        {expandedId === do_.id && (
                          <tr className="hidden">
                            <td
                              colSpan="9"
                              className="bg-slate-50/70 px-6 py-5"
                            >
                              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                                {/* Meta grid */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5 text-xs">
                                  {[
                                    {
                                      label: "Location",
                                      value: do_.locationName || "—",
                                    },
                                    {
                                      label: "Delivery Date",
                                      value: formatDate(do_.deliveryDate),
                                    },
                                    {
                                      label: "SO Reference",
                                      value: do_.salesOrderNumber || "—",
                                    },
                                    {
                                      label: "Quote Ref.",
                                      value:
                                        do_.quoteNumber &&
                                        do_.quoteNumber !== "N/A"
                                          ? do_.quoteNumber
                                          : "—",
                                    },
                                  ].map((info) => (
                                    <div key={info.label} className="space-y-1">
                                      <span className="text-gray-400 uppercase font-semibold">
                                        {info.label}
                                      </span>
                                      <p className="font-medium text-gray-800">
                                        {info.value}
                                      </p>
                                    </div>
                                  ))}
                                </div>

                                {/* ── Line items ── */}
                                <h4 className="font-bold text-gray-700 text-xs uppercase tracking-wide mb-3 flex items-center gap-1.5">
                                  <Package size={13} /> Delivery Items
                                </h4>
                                <div className="border border-gray-200 rounded-xl overflow-hidden mb-4">
                                  <table className="w-full text-xs text-left text-gray-600">
                                    <thead className="bg-gray-50 text-gray-500 uppercase font-semibold border-b border-gray-100">
                                      <tr>
                                        <th className="px-4 py-2.5">
                                          Item Code
                                        </th>
                                        <th className="px-4 py-2.5">Product</th>
                                        <th className="px-4 py-2.5 text-center text-gray-500">
                                          Expected
                                        </th>
                                        <th className="px-4 py-2.5 text-center text-emerald-600">
                                          Received
                                        </th>
                                        <th className="px-4 py-2.5 text-center">
                                          UOM
                                        </th>
                                        <th className="px-4 py-2.5 text-center text-red-500">
                                          Waiting Item
                                        </th>
                                        {/* ✨ NEW: Production column in line items */}
                                        <th className="px-4 py-2.5 text-center text-emerald-600">
                                          Production
                                        </th>
                                        <th className="px-4 py-2.5 text-right">
                                          Unit Price
                                        </th>
                                        <th className="px-4 py-2.5 text-right">
                                          Line Total
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                      {(do_.deliveryOrderItems || []).map(
                                        (item, idx) => {
                                          const received =
                                            item.quantityReceived ?? 0;
                                          const expected =
                                            item.quantityExpected || 0;
                                          const returned =
                                            expected > 0
                                              ? Math.max(expected - received, 0)
                                              : 0;

                                          // ✨ NEW: Production coverage per line item
                                          const itemProds = prods.filter(
                                            (p) =>
                                              p.deliveryOrderItemId === item.id,
                                          );
                                          const itemProdWeight =
                                            itemProds.reduce(
                                              (s, p) =>
                                                s + (p.totalActualWeight || 0),
                                              0,
                                            );

                                          return (
                                            <tr
                                              key={item.id || idx}
                                              className="hover:bg-gray-50 transition"
                                            >
                                              <td className="px-4 py-2.5">
                                                {item.itemCode ? (
                                                  <span className="inline-flex items-center gap-0.5 text-indigo-600 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded font-mono text-xs">
                                                    <Hash size={9} />
                                                    {item.itemCode}
                                                  </span>
                                                ) : (
                                                  <span className="text-gray-300">
                                                    —
                                                  </span>
                                                )}
                                              </td>
                                              <td className="px-4 py-2.5 font-medium text-gray-800">
                                                {item.productName}
                                                {item.priceType && (
                                                  <p className="text-[9px] text-gray-400 font-bold uppercase">
                                                    {item.priceType}
                                                  </p>
                                                )}
                                              </td>
                                              <td className="px-4 py-2.5 text-center font-medium">
                                                {expected || "—"}
                                              </td>
                                              <td className="px-4 py-2.5 text-center font-bold text-emerald-700">
                                                {received}
                                              </td>
                                              <td className="px-4 py-2.5 text-center text-gray-500">
                                                {item.unitName || "—"}
                                              </td>
                                              <td className="px-4 py-2.5 text-center font-bold text-red-500">
                                                {returned > 0 ? returned : "—"}
                                              </td>

                                              {/* ✨ NEW: Per-line production status */}
                                              <td className="px-4 py-2.5 text-center">
                                                {itemProds.length > 0 ? (
                                                  <div className="flex flex-col items-center gap-0.5">
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-[9px] font-bold">
                                                      <CheckCircle2 size={8} />
                                                      {itemProds.length}{" "}
                                                      record(s)
                                                    </span>
                                                    <span className="text-[9px] text-emerald-600 font-semibold">
                                                      {fmt(itemProdWeight)} kg
                                                    </span>
                                                  </div>
                                                ) : (
                                                  <span className="text-[10px] text-gray-400 italic">
                                                    —
                                                  </span>
                                                )}
                                              </td>

                                              <td className="px-4 py-2.5 text-right">
                                                {formatCurrency(item.unitPrice)}
                                              </td>
                                              <td className="px-4 py-2.5 text-right font-bold text-gray-900">
                                                {formatCurrency(item.lineTotal)}
                                              </td>
                                            </tr>
                                          );
                                        },
                                      )}
                                    </tbody>
                                  </table>
                                </div>

                                {/* Financial summary */}
                                <div className="flex justify-end">
                                  <div className="w-72 bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-2 text-sm">
                                    {do_.shippingCost > 0 && (
                                      <div className="flex justify-between text-gray-500">
                                        <span>Shipping</span>
                                        <span className="font-medium">
                                          + {formatCurrency(do_.shippingCost)}
                                        </span>
                                      </div>
                                    )}
                                    {do_.hasVat && do_.vatAmount > 0 && (
                                      <div className="flex justify-between text-emerald-600 text-xs bg-emerald-50 p-2 rounded-lg font-bold">
                                        <span>VAT (12%)</span>
                                        <span>
                                          {formatCurrency(do_.vatAmount)}
                                        </span>
                                      </div>
                                    )}
                                    {do_.hasEwt && do_.ewtAmount > 0 && (
                                      <div className="flex justify-between text-orange-600 text-xs bg-orange-50 p-2 rounded-lg font-bold">
                                        <span>EWT (1%)</span>
                                        <span>
                                          − {formatCurrency(do_.ewtAmount)}
                                        </span>
                                      </div>
                                    )}
                                    <div className="border-t border-gray-200 pt-2 flex justify-between font-bold text-gray-900">
                                      <span>Total</span>
                                      <span>
                                        {formatCurrency(do_.totalAmount)}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* Remarks */}
                                {do_.remarks && (
                                  <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                                      Audit Trail
                                    </p>
                                    <p className="text-xs text-gray-500 italic break-words">
                                      {do_.remarks}
                                    </p>
                                  </div>
                                )}

                                {/* ✨ NEW: Productions Section */}
                                <div className="mt-4 border-t border-gray-100 pt-4">
                                  <DOProductionSection
                                    do_={do_}
                                    productions={prods}
                                    isLoading={productionsLoading.has(do_.id)}
                                  />
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}

                        {/* ── Expanded Detail for Delivery Order ── */}
                        {selectedDO && expandedId === do_.id && (
                          <tr>
                            <td colSpan="9" className="bg-slate-50/70 px-6 py-5">
                              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                                
                                {/* 1. Meta Grid (Top Cards) */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                  <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                                    <span className="text-[10px] text-slate-400 uppercase font-bold flex items-center gap-1.5 mb-1">
                                      <Hash size={12} /> DO Number
                                    </span>
                                    <p className="text-sm font-semibold text-slate-800">{selectedDO.deliveryOrderNumber}</p>
                                  </div>
                                  <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                                    <span className="text-[10px] text-slate-400 uppercase font-bold flex items-center gap-1.5 mb-1">
                                      <Calendar size={12} /> Delivery Date
                                    </span>
                                    <p className="text-sm font-semibold text-slate-800">{formatDate(selectedDO.deliveryDate)}</p>
                                  </div>
                                  <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                                    <span className="text-[10px] text-slate-400 uppercase font-bold flex items-center gap-1.5 mb-1">
                                      <User size={12} /> Customer
                                    </span>
                                    <p className="text-sm font-semibold text-slate-800">{selectedDO.customerName}</p>
                                  </div>
                                  <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                                    <span className="text-[10px] text-slate-400 uppercase font-bold flex items-center gap-1.5 mb-1">
                                      <MapPin size={12} /> Location
                                    </span>
                                    <p className="text-sm font-semibold text-slate-800">{selectedDO.locationName}</p>
                                  </div>
                                </div>

                                {/* 2. Detailed Verification Table */}
                                <div className="overflow-hidden rounded-xl border border-slate-200 shadow-sm bg-white mb-6">
                                  <table className="w-full text-xs text-left">
                                    <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider font-bold border-b border-slate-200">
                                      <tr>
                                        <th className="px-6 py-3">Product Details</th>
                                        <th className="px-4 py-3 text-center">Expected</th>
                                        <th className="px-4 py-3 text-center text-emerald-600">Received</th>
                                        <th className="px-4 py-3 text-center">UOM</th>
                                        <th className="px-4 py-3 text-center text-red-500">Waiting</th>
                                        <th className="px-4 py-3 text-center text-emerald-600">Production</th>
                                        <th className="px-4 py-3 text-right">Unit Price</th>
                                        <th className="px-6 py-3 text-right">Line Total</th>
                                      </tr>
                                    </thead>

                                    <tbody className="divide-y divide-slate-100">
                                      {selectedDO.soReference?.map((so, soIdx) => (
                                        <React.Fragment key={so.soNumber || soIdx}>
                                          {/* SO Reference Group Header */}
                                          <tr className="bg-indigo-50/50 border-y border-indigo-100">
                                            <td colSpan="8" className="px-4 py-2.5">
                                              <div className="flex items-center gap-5">
                                                <div className="flex items-center gap-1.5 px-2 py-1 bg-white border border-indigo-200 rounded shadow-sm text-indigo-700 font-bold">
                                                  <FileText size={12} /> {so.soNumber}
                                                </div>
                                                <span className="text-[11px] text-slate-500 italic">Originating Sales Order</span>
                                              </div>
                                            </td>
                                          </tr>

                                          {/* Line Items */}
                                          {so.salesOrderItems?.map((line, lineIdx) => {
                                            // Production coverage logic
                                            const itemProds = prods?.filter(p => p.deliveryOrderItemId === line.id) || [];
                                            const itemProdWeight = itemProds.reduce((s, p) => s + (p.totalActualWeight || 0), 0);
                                            
                                            return (
                                              <tr key={line.id || lineIdx} className="hover:bg-slate-50 transition-colors group">
                                                {/* Product */}
                                                <td className="px-6 py-4">
                                                  <div className="flex flex-col">
                                                    <span className="text-sm font-bold text-slate-700 group-hover:text-indigo-600 transition-colors">
                                                      {line.productName}
                                                    </span>
                                                    <span className="text-[10px] font-mono text-slate-400 mt-0.5">
                                                      {line.itemCode ? `Code: ${line.itemCode}` : `ID: ${line.productId}`}
                                                    </span>
                                                  </div>
                                                </td>

                                                {/* Expected */}
                                                <td className="px-4 py-4 text-center font-medium text-slate-600">
                                                  {line.quantity}
                                                </td>

                                                {/* Received */}
                                                <td className="px-4 py-4 text-center font-black text-emerald-700 tabular-nums text-sm">
                                                  {line.deliveredQty}
                                                </td>

                                                {/* UOM */}
                                                <td className="px-4 py-4 text-center text-slate-500 font-medium">
                                                  {line.unitName || "—"}
                                                </td>

                                                {/* Waiting */}
                                                <td className="px-4 py-4 text-center font-bold text-red-500 tabular-nums">
                                                  {line.remainingQty > 0 ? line.remainingQty : "—"}
                                                </td>

                                                {/* Production Status */}
                                                <td className="px-4 py-4 text-center">
                                                  {itemProds.length > 0 ? (
                                                    <div className="flex flex-col items-center">
                                                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded text-[9px] font-bold">
                                                        <CheckCircle2 size={10} /> {fmt(itemProdWeight)} kg
                                                      </span>
                                                    </div>
                                                  ) : (
                                                    <span className="text-[10px] text-slate-300 italic">No record</span>
                                                  )}
                                                </td>

                                                {/* Unit Price */}
                                                <td className="px-4 py-4 text-right text-slate-500">
                                                  {formatCurrency(line.unitPrice)}
                                                </td>

                                                {/* Line Total */}
                                                <td className="px-6 py-4 text-right font-bold text-slate-900">
                                                  {formatCurrency(line.lineTotal)}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </React.Fragment>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>

                                {/* 3. Summary Section (Remarks + Financial Box) */}
                                <div className="flex flex-col md:flex-row justify-between gap-6 pt-6 border-t border-slate-100">
                                  <div className="flex-1">
                                    {selectedDO.remarks && (
                                      <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Audit Trail / Remarks</span>
                                        <p className="text-xs text-slate-600 italic leading-relaxed">{selectedDO.remarks}</p>
                                      </div>
                                    )}
                                  </div>

                                  <div className="w-full md:w-80 space-y-2 bg-slate-50 p-4 rounded-xl border border-slate-200">
                                    {selectedDO.shippingCost > 0 && (
                                      <div className="flex justify-between text-xs text-slate-500">
                                        <span>Shipping Cost</span>
                                        <span className="font-medium">+{formatCurrency(selectedDO.shippingCost)}</span>
                                      </div>
                                    )}
                                    {selectedDO.hasVat && (
                                      <div className="flex justify-between text-xs text-emerald-600 font-bold bg-emerald-50/50 px-2 py-1.5 rounded">
                                        <span>VAT (12%)</span>
                                        <span>{formatCurrency(selectedDO.vatAmount || 0)}</span>
                                      </div>
                                    )}
                                    {selectedDO.hasEwt && (
                                      <div className="flex justify-between text-xs text-orange-600 font-bold bg-orange-50/50 px-2 py-1.5 rounded">
                                        <span>EWT (1%)</span>
                                        <span>-{formatCurrency(selectedDO.ewtAmount || 0)}</span>
                                      </div>
                                    )}
                                    <div className="pt-2 border-t border-slate-300 flex justify-between items-baseline">
                                      <span className="text-sm font-bold text-slate-800">Grand Total</span>
                                      <span className="text-xl font-black text-indigo-700">
                                        {formatCurrency(selectedDO.totalAmount)}
                                      </span>
                                    </div>
                                    <div className="text-[10px] text-center text-slate-400 font-bold uppercase tracking-widest pt-2">
                                      Status: {selectedDO.status}
                                    </div>
                                  </div>
                                </div>

                                {/* 4. Production Section Component */}
                                <div className="mt-6 pt-6 border-t border-slate-100">
                                  <DOProductionSection
                                    do_={selectedDO}
                                    productions={prods}
                                    isLoading={productionsLoading.has(selectedDO.id)}
                                  />
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {currentDOs.length === 0 && (
                    <tr>
                      <td colSpan="9" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center">
                          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                            <AlertCircle size={22} className="text-gray-400" />
                          </div>
                          <p className="text-gray-700 font-semibold">
                            No delivery orders found
                          </p>
                          <p className="text-gray-400 text-sm mt-1">
                            {searchTerm
                              ? "Try a different search term."
                              : `No ${activeTab !== "All" ? activeTab.toLowerCase() : ""} orders.`}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ── Mobile Cards ── */}
            <div className="lg:hidden space-y-3">
              {currentDOs.map((do_) => {
                // ✨ NEW: production status for mobile cards
                const prods = getProductionsForDO(do_.id);
                const prodStatus = getProductionStatus(do_);

                return (
                  <div
                    key={do_.id}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                  >
                    <div
                      className="p-4 cursor-pointer"
                      onClick={() =>
                        toggleExpandDO(do_.id)
                      }
                    >
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className="p-1.5 bg-blue-50 rounded-lg shrink-0">
                            <Truck size={15} className="text-blue-600" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-bold text-gray-900 text-sm">
                              {do_.deliveryOrderNumber}
                            </h3>
                            <p className="text-xs text-gray-500 truncate">
                              {do_.customerName}
                            </p>
                            {do_.salesOrderNumber && (
                              <p className="text-xs text-blue-600">
                                SO: {do_.salesOrderNumber}
                              </p>
                            )}
                            {/* ✨ NEW: Mobile production badge */}
                            {prodStatus !== "none" && (
                              <div className="mt-1">
                                <ProductionStatusBadge
                                  status={prodStatus}
                                  count={prods.length}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <StatusBadge
                            status={do_.isVoid ? "Voided" : do_.status}
                          />
                          <p className="font-bold text-gray-900 text-sm mt-1.5">
                            {formatCurrency(do_.totalAmount)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {expandedId === do_.id && (
                      <div className="border-t border-gray-100 bg-gray-50 p-4 space-y-3">
                        {/* ✨ NEW: Mobile production records */}
                        <DOProductionSection
                          do_={do_}
                          productions={prods}
                          isLoading={productionsLoading.has(do_.id)}
                        />

                        <button
                          onClick={() => triggerPrint(do_)}
                          className="w-full py-2 bg-white text-gray-600 rounded-lg text-xs font-medium border border-gray-200 flex items-center justify-center gap-1.5"
                        >
                          <Printer size={14} /> Print DO
                        </button>

                        {do_.status?.toLowerCase() === "sold" &&
                          !do_.isVoid && (
                            <button
                              onClick={() => setDispatchTarget(do_)}
                              className="w-full py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg text-xs font-medium transition flex items-center justify-center gap-1.5 shadow-sm"
                            >
                              For Dispatch <Send size={14} />
                            </button>
                          )}
                      </div>
                    )}
                  </div>
                );
              })}
              {currentDOs.length === 0 && (
                <div className="text-center py-14 text-gray-400 bg-white rounded-2xl border border-gray-100">
                  <AlertCircle size={36} className="mx-auto mb-2 opacity-40" />
                  <p className="font-medium text-gray-500">
                    No delivery orders found.
                  </p>
                </div>
              )}
            </div>

            <div className="mt-5">
              <Pagination
                itemsPerPage={itemsPerPage}
                totalItems={filtered.length}
                currentPage={currentPage}
                paginate={(p) => setCurrentPage(p)}
              />
            </div>
          </>
        )}
      </div>

      <SyncToAccountingModal 
        isOpen={showSyncToAccountingForm}
        onClose={closeSyncToAccountingForm}
        onConfirm={handleSyncToAccounting}
      />

      <AverageCostModal 
        isOpen={showAverageCostConfig}
        items={itemsWithoutAverageCost}
        onClose={closeAverageCostConfig}
        onSave={handleSaveCostsAndContinue}
      />
    </div>
  );
};

const SyncToAccountingModal = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const today = new Date().toISOString().split("T")[0];
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);

  if (!isOpen) return null;

  const handleSubmit = () => {
    onConfirm(dateFrom, dateTo);
    onClose();
  };
  
  const inputClass =
    "block w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-900 transition-all focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-200 outline-none";
  const labelClass =
    "mb-1 ml-0.5 block text-[10px] font-black uppercase tracking-widest text-gray-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
        {/* Header */}
        <div className="bg-indigo-600 px-6 py-4 flex justify-between items-center">
          <h2 className="text-white text-lg font-bold flex items-center gap-2">
            Sync To Accounting
          </h2>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-gray-600 mb-4">
            You are about to sync data to accounting.
            <br />
            <br />
            <strong>Please fill up fields to filter data</strong>
          </p>

          <div className="flex flex-col gap-3">
            <div>
              <label className={labelClass}>
                Order Date From
                <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className={`${inputClass} !border-indigo-300`}
              />
            </div>
            <div>
              <label className={labelClass}>
                Order Date To
                <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className={`${inputClass} !border-indigo-300`}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 shadow-md"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export const AverageCostModal = ({ isOpen, items, onClose, onSave }) => {
  // Local state to store the user-inputted costs
  // Structure: { itemId: costValue }
  const [inputs, setInputs] = useState({});

  // Initialize inputs when items change
  useEffect(() => {
    if (items && items.length > 0) {
      const initialInputs = {};
      items.forEach(item => {
        initialInputs[item.id] = ""; // Start empty or with 0
      });
      setInputs(initialInputs);
    }
  }, [items]);

  const handleInputChange = (id, value) => {
    setInputs(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSaveClick = () => {
    const updatedItems = items.map(item => ({
      id: item.id,
      itemCode: item.itemCode,
      averageCost: parseFloat(inputs[item.id]) || 0
    }));

    const hasInvalidItem = updatedItems.some(
      item => item.averageCost <= 0
    );

    if (hasInvalidItem) {
      alert("Please enter an Average Cost for all items.");
      return;
    }

    onSave(updatedItems);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
          <h2 className="text-xl font-bold text-red-600">Missing Average Costs</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>

        {/* Body */}
        <div className="p-4 overflow-y-auto flex-1">
          <p className="mb-4 text-sm text-gray-600">
            The following items have no average cost yet. Please enter initial cost to proceed.
          </p>
          
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item Code</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Brand</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32">Avg Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {items.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-2 text-sm font-mono">{item.itemCode}</td>
                  <td className="px-4 py-2 text-sm">{item.itemDescription || ""}</td>
                  <td className="px-4 py-2 text-sm">{item.brandName || ""}</td>
                  <td className="px-4 py-2">
                    <input
                      type="number"
                      step="0.01"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                      value={inputs[item.id] || ""}
                      onChange={(e) => handleInputChange(item.id, e.target.value)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveClick}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Update Costs & Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default AllDeliveryOrders;
